// Login page for the client portal
import { useState, useEffect } from 'react';
import { Form, Link, useActionData } from '@remix-run/react';

// login utils
import { createUserSession, login } from '~/session.server';
import { toast } from 'react-toastify';
import { json } from '@remix-run/node';
import { getAccount } from '../api.server';
// components
import SubmitButton from '../components/Buttons/SubmitButton';

// assets
import banner from '../images/homeHeroBanner.png';
import UnderLineLink from '../components/Links/UnderLineLink';

export const meta = () => [
  { title: 'Colonial Flowers' },
  { name: 'description', content: 'Welcome to Colonial Flowers Portal' },
];

export async function action({ request }) {
  try {
    const formData = await request.formData();
    const email = formData.get('email');
    const password = formData.get('password');
    const errors = { inputErrors: {} };
    if (!email) {
      errors.inputErrors = { email: 'Please enter your email' };
    }
    if (!password) {
      errors.inputErrors = { password: 'Please enter your password' };
    }

    if (Object.keys(errors.inputErrors).length > 0) {
      return json({ errors }, { status: 400 });
    }

    const redirectTo = '/home';
	const guid = await login(email, password);

	if (!guid) {
	  throw new Error('Invalid email or password');
	}

	const userAccount = await getAccount(guid);
    if (userAccount === '') {
      throw new Error(
        'No account found for this email. Please request an account.',
      );
    }
    // Handle address ID - it can be an array or a single item
    const shippingAddresses =
      userAccount['a:ShippingAddresses']['a:WebShippingAddress'];

    // Check if shippingAddresses is an array
    let accountID;
    if (Array.isArray(shippingAddresses)) {
      // If it's an array, take the first item for now
      // TODO: Add logic to handle multiple addresses properly
      accountID = shippingAddresses[0]['a:AddressID'];
    } else {
      // If it's not an array, use it directly
      accountID = shippingAddresses['a:AddressID'];
    }
    return createUserSession({
      redirectTo,
      remember: false,
      request,
      userId: { guid, accountID },
    });
  } catch (error) {
    return json({ errors: 'Invalid email or password.' }, { status: 401 });
  }
}

export default function Index() {
  const actionData = useActionData();
  const [error, setError] = useState('');
  const [inputErrors, setInputErrors] = useState({});

  useEffect(() => {
    if (actionData?.errors) {
      setError(actionData?.errors || '');
      setInputErrors(actionData?.errors?.inputErrors || {});
    }
  }, [actionData]);

  return (
    <main className="flex flex-col lg:flex-row h-screen">
      <section
        id="heroImage"
        className="w-full lg:w-1/2 h-1/4 sm:h-2/5 lg:h-full"
      >
        <img
          src={banner}
          alt="purple flowers in hanging baskets"
          className="h-full w-full object-cover"
        />
      </section>
      <section
        id="login"
        className="w-full lg:w-1/2 h-1/2 lg:h-full flex flex-col items-center lg:items-start justify-center px-4 lg:px-24 my-auto lg:my-0"
      >
        <Form method="post">
          <fieldset className="flex flex-col w-full max-w-sm mx-auto lg:mx-0 lg:mt-32">
            <h1 className="text-4xl mb-8 uppercase">Client Portal</h1>

            <label htmlFor="email" className="mb-1 font-light">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              className="border p-3 border-form-gray mb-4"
              required
            />
            {inputErrors?.email && (
              <p className="text-red">{inputErrors.email}</p>
            )}
            <label htmlFor="password" className="mb-1 font-light">
              Password
            </label>
            <input
              type="password"
              name="password"
              id="password"
              className="border p-3 border-form-gray mb-8"
              required
            />
            {inputErrors?.password && (
              <p className="text-red">{inputErrors.password}</p>
            )}
            <div className="flex gap-16 items-center mb-12 lg:mb-24">
              <SubmitButton>Sign In</SubmitButton>
              <UnderLineLink to="/reset-password">
                Forgot Password?
              </UnderLineLink>
            </div>
            {error && <p className="text-red -mt-12 lg:-mt-20 mb-8">{error}</p>}
          </fieldset>
        </Form>
        <div className="extra-links flex flex-col mx-auto lg:mx-0">
          <div className="flex gap-5 mb-8 lg:mb-56">
            <p className="font-light">Don't have an account?</p>
            <UnderLineLink to="https://colonialfloristsltd.com/request-an-account">
              Request an Account
            </UnderLineLink>
          </div>
          <div className="flex gap-3">
			<a
			  href="https://colonialfloristsltd.com/privacy-policy"
			  className="font-light"
			  target="_blank"
			  rel="noopener noreferrer"
			>
			  Privacy Policy
			</a>

			<span className="font-light">|</span>

			<a
			  href="https://colonialfloristsltd.com/privacy-policy"
			  className="font-light"
			  target="_blank"
			  rel="noopener noreferrer"
			>
			  Terms &amp; Conditions
			</a>
          </div>
        </div>
      </section>
    </main>
  );
}
